import { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import { getGrievanceReport } from "../../services/grievance";

const card = {
  background: "#fff",
  border: "1px solid #e3e6ec",
  borderRadius: "10px",
  padding: "14px 16px",
};

// A count that opens the matching grievance list when clicked. Without an
// onClick it renders as plain text (e.g. averages, which have no list).
const clickableProps = (onClick) =>
  onClick
    ? {
        onClick,
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

const Stat = ({ label, value, hint, danger, onClick }) => (
  <div
    {...clickableProps(onClick)}
    title={onClick ? "Click to view these grievances" : undefined}
    style={{
      ...card,
      flex: "1 1 160px",
      cursor: onClick ? "pointer" : "default",
    }}
  >
    <div style={{ fontSize: "12px", color: "#666" }}>{label}</div>
    <div
      style={{
        fontSize: "26px",
        fontWeight: 600,
        color: danger && value > 0 ? "#c0392b" : onClick ? "#4a5aa8" : "inherit",
        textDecoration: onClick ? "underline" : "none",
      }}
    >
      {value ?? "-"}
    </div>
    {hint && <div style={{ fontSize: "11.5px", color: "#888" }}>{hint}</div>}
  </div>
);

// One row per bucket with a proportional bar — a plain table is easier to
// read exactly than a pie, and needs no chart library. Clicking a row opens
// the grievances in that bucket.
const Breakdown = ({ title, rows, onSelect }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={{ ...card, flex: "1 1 320px" }}>
      <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "10px" }}>
        {title}
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: "12.5px", color: "#666" }}>No data.</div>
      ) : (
        rows.map((row) => {
          const canOpen = onSelect && row.key !== null && row.key !== undefined;
          return (
            <div
              key={row.key ?? row.label}
              {...clickableProps(canOpen ? () => onSelect(row) : undefined)}
              title={canOpen ? "Click to view these grievances" : undefined}
              style={{
                marginBottom: "8px",
                cursor: canOpen ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12.5px",
                }}
              >
                <span>{row.label}</span>
                <strong
                  style={
                    canOpen
                      ? { color: "#4a5aa8", textDecoration: "underline" }
                      : undefined
                  }
                >
                  {row.count}
                </strong>
              </div>
              <div style={{ background: "#eef0fa", borderRadius: "4px", height: "6px" }}>
                <div
                  style={{
                    width: `${(row.count / max) * 100}%`,
                    background: "#4a5aa8",
                    borderRadius: "4px",
                    height: "6px",
                  }}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// HR Manager / Admin dashboard. onOpenList(filters) switches to the
// Grievance tab with those list filters applied; the dashboard's own
// Raised From / To range is carried along so the list matches the count.
const GrievanceDashboard = ({ onOpenList }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchReport = async (from = fromDate, to = toDate) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const res = await getGrievanceReport(params);
      setReport(res.data?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, []);

  const openList = (filters) =>
    onOpenList({
      ...filters,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    });

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
          margin: "12px 0 16px",
        }}
      >
        <TextField
          type="date"
          size="small"
          label="Raised From"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          sx={{ backgroundColor: "white" }}
        />
        <TextField
          type="date"
          size="small"
          label="Raised To"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          sx={{ backgroundColor: "white" }}
        />
        <Button variant="success" onClick={() => fetchReport()}>
          Apply
        </Button>
        <Button
          variant="dark"
          onClick={() => {
            setFromDate("");
            setToDate("");
            fetchReport("", "");
          }}
        >
          Clear
        </Button>
      </div>

      {loading ? (
        <p style={{ color: "#666" }}>Loading...</p>
      ) : !report ? (
        <p style={{ color: "#666" }}>No data.</p>
      ) : (
        <>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
            <Stat
              label="Total grievances"
              value={report.total}
              onClick={() => openList({})}
            />
            <Stat
              label="Open"
              value={report.open}
              onClick={() => openList({ open: true })}
            />
            <Stat
              label="Overdue"
              value={report.overdue}
              danger
              onClick={() => openList({ overdue: true })}
            />
            <Stat
              label="Avg. time to resolve"
              value={report.avg_resolution_days === null ? "-" : `${report.avg_resolution_days} d`}
              hint="First resolution"
            />
            <Stat
              label="Resolved within SLA"
              value={
                report.resolved_within_sla_pct === null
                  ? "-"
                  : `${report.resolved_within_sla_pct}%`
              }
            />
            <Stat
              label="Reopened"
              value={report.reopened}
              hint={`${report.reopen_rate_pct}% of grievances`}
              onClick={() => openList({ reopened: true })}
            />
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Breakdown
              title="By status"
              rows={report.by_status.filter((r) => r.count > 0)}
              onSelect={(row) => openList({ status: String(row.key) })}
            />
            <Breakdown
              title="By category"
              rows={report.by_category}
              onSelect={(row) => openList({ category_id: row.key })}
            />
            <Breakdown
              title="By priority"
              rows={report.by_priority}
              onSelect={(row) => openList({ priority: row.key })}
            />
            <Breakdown
              title="By department"
              rows={report.by_department}
              onSelect={(row) => openList({ department: row.key })}
            />
          </div>

          <p style={{ fontSize: "12px", color: "#888", marginTop: "14px" }}>
            Response targets: High 3 days, Medium 5 days, Low 7 days from when a
            grievance is raised (restarted when it is reopened).
          </p>
        </>
      )}
    </div>
  );
};

export default GrievanceDashboard;
